import { useState } from 'react';
import { Link, Badge, DataTable, Filters, Tooltip, Button, Box, Select } from '@shopify/polaris';
import { formatToCurrency, getDocPdfLinkForListViewIncoming, getTableFooter, truncate, patchRequest } from '../../../shared/components/functions';
import { addFilters, applyFilters, handleFilterChange, removeFilter, handleFiltersClearAll } from '../../../shared/components/listFilter';
import { CreditCardIcon, CreditCardPercentIcon } from '@shopify/polaris-icons';
import { useAuthStore, useLookupStore } from '../../../context/useStore';
import ApprovalStatusBadge from '../../../shared/components/approvals/ApprovalStatusBadge';
import InvoicePdfPreviewButton from '../../../shared/components/InvoicePdfPreviewButton';
import PaymentIntentBadge from '../../../shared/components/banking/PaymentIntentBadge';

export default function InvoicesInTable({
  items,
  page,
  setPage,
  filters,
  setFilters,
  navigate,
  setInvoiceCollectionModalOpen,
  setCollectionIvoice,
  setFullInvoiceCollection,
  filterLoading,
  refreshData
}) {
  const { user } = useAuthStore();
  const businessunits = useLookupStore((state) => state.businessunits);
  const [editingBusinessUnitId, setEditingBusinessUnitId] = useState(null);
  const businessUnitOptions = [
    { label: 'No BU', value: '' },
    ...businessunits
      .filter((bu) => bu.IsActive)
      .sort((a, b) => (a.SortOrder > b.SortOrder ? 1 : a.SortOrder < b.SortOrder ? -1 : a.Name.localeCompare(b.Name)))
      .map((bu) => ({ value: String(bu.BusinessUnitID), label: `${bu.Code} (${bu.Name})` }))
  ];

  const handleBusinessUnitChange = async (invoiceId, businessUnitId) => {
    if (!invoiceId) return;
    const normalizedBusinessUnitId = businessUnitId === '' ? null : Number(businessUnitId);

    await patchRequest(
      `/api/invoices_in/${invoiceId}/business-unit`,
      { BusinessUnitID: normalizedBusinessUnitId },
      () => {},
      () => console.error('Failed to update business unit')
    );

    if (refreshData) refreshData();
  };

  const handleBusinessUnitSelect = async (invoiceId, value) => {
    await handleBusinessUnitChange(invoiceId, value);
    setEditingBusinessUnitId(null);
  };

  const rows = Array.isArray(items)
    ? items.map((item) => {
        const displayButtons = !item.status; // show payment actions whenever invoice is not paid
        return [
          <div style={{ display: 'flex', marginLeft: '-10px', gap: '5px' }}>
            {!item.processed && item.S3ObjectKeys ? '🔔' : null}
            <Link removeUnderline url={'/invoices_in/' + item.faktuurinvolgnummer}>
              {item.FactuurInNummer}
            </Link>
          </div>,
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {editingBusinessUnitId === item.faktuurinvolgnummer ? (
              <div style={{ width: '170px', transform: 'scale(0.82)', transformOrigin: 'top left', marginTop: '-10px', marginBottom: '-10px' }}>
                <Select
                  label="Business Unit"
                  labelHidden
                  options={businessUnitOptions}
                  value={item.BusinessUnitID != null ? String(item.BusinessUnitID) : ''}
                  onChange={(value) => {
                    handleBusinessUnitSelect(item.faktuurinvolgnummer, value);
                  }}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEditingBusinessUnitId(item.faktuurinvolgnummer)}
                style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}
                title="Click to change business unit"
              >
                {item.business_unit_code ? <Badge>{item.business_unit_code}</Badge> : <Badge tone="warning">BU ?</Badge>}
              </button>
            )}
          </div>,
          item.PeppolStatus ? <Badge tone="info">{item.PeppolStatus}</Badge> : <Badge>man entry</Badge>,
          // getDocPdfLinkForListViewIncoming(item, item.supplier_number, item.FactuurInNummer, user, 'Factuur In', 'FI'),
          <div style={{ marginTop: '-5px', marginBottom: '-5px' }}>
            <InvoicePdfPreviewButton s3ObjectKeys={item.S3ObjectKeys} buttonLabel="View PDF" buttonSize="slim" buttonEmoji="👀" modalTitle="Invoice PDF Preview" />
          </div>,
          <Link removeUnderline url={'/companies/' + item.LeverancierNummer}>
            {item.supplier_land_id === 300 && '🇧🇪 - '}
            {item.supplier_name?.substring(0, 30)}
          </Link>,
          item.datum ? String(item.datum)?.substring(0, 10) : '',
          item.datumbetaling ? String(item.datumbetaling)?.substring(0, 10) : '',
          item.faktuurref,
          formatToCurrency(item.Bedragfaktuur),
          formatToCurrency(item.bedragopen),
          formatToCurrency(item.BedragExclBtw ?? item.Bedragfaktuur),
          item.datumbetalingout ? String(item.datumbetalingout)?.substring(0, 10) : '',
          truncate(item.Incasseren, 12, false),
          <Tooltip content={item.memo || ''}>
            <span>{truncate(item.memo, 25, false)}</span>
          </Tooltip>,
          <ApprovalStatusBadge status={item.ApprovalStatus} assigneeAfkorting={item.assignee_afkorting} lines={item.approvals} />,
          // paid | pending bank payment (Ponto payment intent, invoice still open) | not paid
          item.status ? (
            <Badge tone="success">Paid</Badge>
          ) : item.payment_intent_state ? (
            <PaymentIntentBadge state={item.payment_intent_state} amount={item.payment_intent_amount} createdAt={item.payment_intent_created_at} id={item.payment_intent_id} />
          ) : (
            <Badge tone="warning">Not paid</Badge>
          ),
          displayButtons ? (
            <div style={{ margin: '-4px 0' }}>
              <Tooltip content={item.payment_intent_state ? 'Mark as full paid (a bank payment is already pending)' : 'Mark as full paid'}>
                <Button
                  size="slim"
                  icon={CreditCardIcon}
                  onClick={() => {
                    setInvoiceCollectionModalOpen(true);
                    setCollectionIvoice(item);
                    setFullInvoiceCollection(true);
                  }}
                />
              </Tooltip>
              <Tooltip content="Mark as partially paid">
                <Button
                  size="slim"
                  icon={CreditCardPercentIcon}
                  onClick={() => {
                    setInvoiceCollectionModalOpen(true);
                    setCollectionIvoice(item);
                    setFullInvoiceCollection(false);
                  }}
                />
              </Tooltip>
            </div>
          ) : (
            ''
          )
        ];
      })
    : [];

  const totals =
    items.length > 0
      ? [
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          formatToCurrency(items.reduce((p, c) => p + c.Bedragfaktuur, 0)),
          formatToCurrency(items.reduce((p, c) => p + c.bedragopen, 0)),
          formatToCurrency(items.reduce((p, c) => p + (c.BedragExclBtw ?? c.Bedragfaktuur ?? 0), 0)),
          '',
          '',
          '',
          '',
          '',
          ''
        ]
      : null;

  return (
    <Box>
      <Filters
        queryValue={filters.q.value}
        filters={addFilters(filters, setFilters, navigate, setPage)}
        appliedFilters={applyFilters(filters, setFilters, navigate, setPage)}
        loading={filterLoading}
        onQueryChange={(e) => handleFilterChange(e, filters, setFilters, 'q', navigate, setPage)}
        onQueryClear={() => removeFilter('q', filters, setFilters, navigate, setPage)}
        autoComplete="off"
        queryPlaceholder="Filter invoices on supplier name, number, value, reference and notes"
        onClearAll={() => handleFiltersClearAll(filters, setFilters, navigate, setPage)}
      />
      <DataTable
        verticalAlign="middle"
        stickyHeader
        increasedTableDensity
        columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text', 'text', 'text', 'text', 'text']}
        totals={totals}
        showTotalsInFooter
        headings={[
          <b>Number</b>,
          <b>BU</b>,
          <b>Peppol</b>,
          <b>PDF</b>,
          <b>Supplier</b>,
          <b>Date invoice</b>,
          <b>Date expire</b>,
          <b>Reference</b>,
          <b>Value (incl. VAT)</b>,
          <b>Open (incl. VAT)</b>,
          <b>Value (excl. VAT)</b>,
          <b>Date payment</b>,
          <b>Incasseren</b>,
          <b>Note</b>,
          <b>Approval</b>,
          <b>Paid</b>,
          <b>Payment</b>
        ]}
        rows={rows}
        footerContent={getTableFooter(rows, page, setPage, 'incoming invoices')}
      />
    </Box>
  );
}
